/**
 * Change Orders List Page
 * BuilderBel Construction Project Management System
 */
import {
  AlertTriangle,
  Ban,
  Check,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  FileText,
  Inbox,
  Pencil,
  Plus,
  Send,
  Trash2,
  XCircle,
} from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { ChangeOrderDrawer, SendChangeOrderModal } from '../components/changeOrders';
import { SearchableDropdown } from '../components/forms';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  ActionMenu,
  type ActionMenuItem,
  EmptyState,
  ModuleLayout,
  PriorityBadge,
  StatusBadge,
} from '../components/ui';
import { useToast } from '../contexts/ToastContext';
import { useCurrentProjectId } from '../hooks/useProjectRedux';
import {
  downloadChangeOrderPdf,
  useApproveInternalMutation,
  useDeleteChangeOrderMutation,
  useGetChangeOrderLogQuery,
  useGetChangeOrdersQuery,
  useRejectInternalMutation,
  useSubmitForInternalApprovalMutation,
  useVoidChangeOrderMutation,
} from '../services/changeOrderApi';
import type {
  ChangeOrder,
  ChangeOrderPriority,
  ChangeOrderStatus,
  ChangeOrderType,
} from '../types/changeOrder';

// Status filter options - matches backend enum
const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending_internal', label: 'Pending Internal' },
  { value: 'pending_client', label: 'Pending Client' },
  { value: 'negotiating', label: 'Negotiating' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'void', label: 'Void' },
];

// Type filter options - matches backend enum
const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'owner_requested', label: 'Owner Requested' },
  { value: 'unforeseen_condition', label: 'Unforeseen Condition' },
  { value: 'design_error', label: 'Design Error' },
  { value: 'scope_clarification', label: 'Scope Clarification' },
  { value: 'value_engineering', label: 'Value Engineering' },
  { value: 'allowance_adjustment', label: 'Allowance Adjustment' },
];

// Priority filter options
const PRIORITY_OPTIONS = [
  { value: '', label: 'All Priorities' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

// Sort options - matches backend listQuerySchema
const SORT_OPTIONS = [
  { value: 'createdAt', label: 'Created Date' },
  { value: 'requestDate', label: 'Request Date' },
  { value: 'totalAmount', label: 'Amount' },
  { value: 'coNumber', label: 'CO Number' },
  { value: 'status', label: 'Status' },
  { value: 'title', label: 'Title' },
];

// Get status variant for StatusBadge - matches backend status enum
const getStatusVariant = (
  status: ChangeOrderStatus
): 'success' | 'warning' | 'error' | 'info' | 'purple' | 'neutral' => {
  switch (status) {
    case 'approved':
      return 'success';
    case 'pending_internal':
    case 'pending_client':
    case 'negotiating':
      return 'warning';
    case 'rejected':
    case 'void':
      return 'error';
    case 'draft':
    default:
      return 'neutral';
  }
};

// Get priority variant for PriorityBadge
const getPriorityVariant = (
  priority: ChangeOrderPriority
): 'low' | 'medium' | 'high' | 'critical' => {
  return priority;
};

// Get status label
const getStatusLabel = (status: ChangeOrderStatus): string => {
  const labels: Record<ChangeOrderStatus, string> = {
    draft: 'Draft',
    pending_internal: 'Pending Internal',
    pending_client: 'Pending Client',
    negotiating: 'Negotiating',
    approved: 'Approved',
    rejected: 'Rejected',
    void: 'Void',
  };
  return labels[status] || status;
};

// Get type label
const getTypeLabel = (type: ChangeOrderType): string => {
  const labels: Record<ChangeOrderType, string> = {
    owner_requested: 'Owner Requested',
    unforeseen_condition: 'Unforeseen Condition',
    design_error: 'Design Error',
    scope_clarification: 'Scope Clarification',
    value_engineering: 'Value Engineering',
    allowance_adjustment: 'Allowance Adjustment',
  };
  return labels[type] || type;
};

// Format currency
const formatCurrency = (amount: number, currency = 'USD'): string => {
  const absAmount = Math.abs(amount);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(absAmount);
};

// Format date
const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const ChangeOrders: React.FC = () => {
  const projectId = useCurrentProjectId();
  const toast = useToast();

  // State for filters and pagination
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<ChangeOrderStatus | ''>('');
  const [type, setType] = useState<ChangeOrderType | ''>('');
  const [priority, setPriority] = useState<ChangeOrderPriority | ''>('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const limit = 20;

  // State for drawer
  const [selectedCO, setSelectedCO] = useState<ChangeOrder | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit' | 'view'>('create');

  // State for Send Modal
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [sendCO, setSendCO] = useState<ChangeOrder | null>(null);

  // State for dropdown menu
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);

  // API calls
  const {
    data: changeOrdersResponse,
    isLoading,
    isFetching,
    refetch,
  } = useGetChangeOrdersQuery(
    {
      projectId: projectId || '',
      params: {
        page,
        limit,
        search: search || undefined,
        status: status || undefined,
        type: type || undefined,
        priority: priority || undefined,
        sortBy: sortBy as
          | 'createdAt'
          | 'requestDate'
          | 'totalAmount'
          | 'coNumber'
          | 'status'
          | 'title',
        sortOrder,
      },
    },
    { skip: !projectId }
  );

  const { data: logResponse } = useGetChangeOrderLogQuery(
    { projectId: projectId || '' },
    { skip: !projectId }
  );

  const [deleteChangeOrder, { isLoading: isDeleting }] = useDeleteChangeOrderMutation();
  const [submitForInternalApproval] = useSubmitForInternalApprovalMutation();
  const [approveInternal] = useApproveInternalMutation();
  const [rejectInternal] = useRejectInternalMutation();
  const [voidChangeOrder] = useVoidChangeOrderMutation();

  // Extract data
  const changeOrders = useMemo(
    () => changeOrdersResponse?.data?.changeOrders || [],
    [changeOrdersResponse]
  );
  const pagination = changeOrdersResponse?.data?.pagination;
  const summary = changeOrdersResponse?.data?.summary;

  // Computed stats for header
  const headerStats = useMemo(() => {
    if (!summary) return [];
    return [
      {
        title: 'Total COs',
        value: summary.total || 0,
        variant: 'brand' as const,
      },
      {
        title: 'Pending',
        value: summary.totalPending || 0,
        variant: 'warning' as const,
      },
      {
        title: 'Approved',
        value: summary.totalApproved || 0,
        variant: 'success' as const,
      },
      {
        title: 'Total Value',
        value: formatCurrency(summary.totalValue || 0),
        variant: 'info' as const,
      },
    ];
  }, [summary]);

  // Handlers
  const handleCreateCO = useCallback(() => {
    setSelectedCO(null);
    setDrawerMode('create');
    setIsDrawerOpen(true);
  }, []);

  const handleViewCO = useCallback((co: ChangeOrder) => {
    setSelectedCO(co);
    setDrawerMode('view');
    setIsDrawerOpen(true);
  }, []);

  const handleEditCO = useCallback((co: ChangeOrder) => {
    setSelectedCO(co);
    setDrawerMode('edit');
    setIsDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setSelectedCO(null);
  }, []);

  const handleDeleteCO = useCallback(
    async (co: ChangeOrder) => {
      if (!projectId) return;
      if (window.confirm(`Are you sure you want to delete CO ${co.coNumber}?`)) {
        try {
          await deleteChangeOrder({
            projectId,
            changeOrderId: co._id,
          }).unwrap();
          toast.success('Change order deleted');
        } catch (error) {
          console.error('Failed to delete change order:', error);
          toast.error('Failed to delete change order');
        }
      }
    },
    [projectId, deleteChangeOrder, toast]
  );

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  // Handle PDF Download
  const handleDownloadPDF = useCallback(
    async (co: ChangeOrder) => {
      if (!projectId) return;
      try {
        await downloadChangeOrderPdf(projectId, co._id, co.coNumber);
        toast.success('PDF downloaded successfully');
      } catch {
        toast.error('Failed to download PDF');
      }
    },
    [projectId, toast]
  );

  // Handle Send to Client
  const handleOpenSendModal = useCallback((co: ChangeOrder) => {
    setSendCO(co);
    setIsSendModalOpen(true);
  }, []);

  const handleCloseSendModal = useCallback(() => {
    setIsSendModalOpen(false);
    setSendCO(null);
  }, []);

  // Handle Submit for Internal Approval
  const handleSubmitForApproval = useCallback(
    async (co: ChangeOrder) => {
      if (!projectId) return;
      try {
        await submitForInternalApproval({
          projectId,
          changeOrderId: co._id,
        }).unwrap();
        toast.success('Submitted for internal approval');
        refetch();
      } catch {
        toast.error('Failed to submit for approval');
      }
    },
    [projectId, submitForInternalApproval, toast, refetch]
  );

  // Handle Approve Internal
  const handleApproveInternal = useCallback(
    async (co: ChangeOrder) => {
      if (!projectId) return;
      try {
        await approveInternal({
          projectId,
          changeOrderId: co._id,
        }).unwrap();
        toast.success('Change order approved internally');
        refetch();
      } catch {
        toast.error('Failed to approve');
      }
    },
    [projectId, approveInternal, toast, refetch]
  );

  // Handle Reject Internal
  const handleRejectInternal = useCallback(
    async (co: ChangeOrder) => {
      if (!projectId) return;
      const reason = window.prompt('Please provide a reason for rejection:');
      if (!reason) return;
      try {
        await rejectInternal({
          projectId,
          changeOrderId: co._id,
          data: { reason },
        }).unwrap();
        toast.success('Change order rejected');
        refetch();
      } catch {
        toast.error('Failed to reject');
      }
    },
    [projectId, rejectInternal, toast, refetch]
  );

  // Handle Void
  const handleVoid = useCallback(
    async (co: ChangeOrder) => {
      if (!projectId) return;
      const reason = window.prompt('Please provide a reason for voiding this change order:');
      if (reason === null) return; // User cancelled
      if (
        !window.confirm(`Are you sure you want to void CO ${co.coNumber}? This cannot be undone.`)
      )
        return;
      try {
        await voidChangeOrder({
          projectId,
          changeOrderId: co._id,
          data: { reason: reason || undefined },
        }).unwrap();
        toast.success('Change order voided');
        refetch();
      } catch {
        toast.error('Failed to void change order');
      }
    },
    [projectId, voidChangeOrder, toast, refetch]
  );

  // Render filters
  const renderFilters = () => (
    <div className="flex items-center gap-2">
      <SearchableDropdown
        placeholder="Status"
        value={status}
        onChange={value => {
          setStatus(value as ChangeOrderStatus | '');
          setPage(1);
        }}
        options={STATUS_OPTIONS}
        searchable={false}
      />
      <SearchableDropdown
        placeholder="Type"
        value={type}
        onChange={value => {
          setType(value as ChangeOrderType | '');
          setPage(1);
        }}
        options={TYPE_OPTIONS}
        searchable={false}
      />
      <SearchableDropdown
        placeholder="Priority"
        value={priority}
        onChange={value => {
          setPriority(value as ChangeOrderPriority | '');
          setPage(1);
        }}
        options={PRIORITY_OPTIONS}
        searchable={false}
      />
    </div>
  );

  // Render pagination
  const renderPagination = () => {
    if (!pagination || pagination.totalPages <= 1) return null;

    const { page: currentPage, totalPages, total } = pagination;
    const startItem = (currentPage - 1) * limit + 1;
    const endItem = Math.min(currentPage * limit, total);

    return (
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-500">
          Showing {startItem}-{endItem} of {total} results
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-xs text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  };

  // Render row actions dropdown using portal-based ActionMenu
  const renderRowActions = (co: ChangeOrder) => {
    const isMenuOpen = activeMenuId === co._id;

    const menuItems: (ActionMenuItem | 'divider')[] = [
      // View & Edit
      {
        label: 'View Details',
        icon: <Eye className="h-3.5 w-3.5" />,
        onClick: () => handleViewCO(co),
      },
      {
        label: 'Edit',
        icon: <Pencil className="h-3.5 w-3.5" />,
        onClick: () => handleEditCO(co),
        hidden: co.status !== 'draft',
      },
      'divider',
      // Internal Approval Actions
      {
        label: 'Submit for Approval',
        icon: <FileText className="h-3.5 w-3.5" />,
        onClick: () => handleSubmitForApproval(co),
        hidden: co.status !== 'draft',
      },
      {
        label: 'Approve Internally',
        icon: <Check className="h-3.5 w-3.5" />,
        onClick: () => handleApproveInternal(co),
        variant: 'success',
        hidden: co.status !== 'pending_internal',
      },
      {
        label: 'Reject Internally',
        icon: <XCircle className="h-3.5 w-3.5" />,
        onClick: () => handleRejectInternal(co),
        variant: 'warning',
        hidden: co.status !== 'pending_internal',
      },
      // Client Actions
      {
        label: 'Send to Client',
        icon: <Send className="h-3.5 w-3.5" />,
        onClick: () => handleOpenSendModal(co),
        hidden: co.internalApproval?.status !== 'approved' || co.status === 'approved',
      },
      'divider',
      // PDF Export
      {
        label: 'Download PDF',
        icon: <Download className="h-3.5 w-3.5" />,
        onClick: () => handleDownloadPDF(co),
      },
      'divider',
      // Void (for non-draft, non-void statuses)
      {
        label: 'Void Change Order',
        icon: <Ban className="h-3.5 w-3.5" />,
        onClick: () => handleVoid(co),
        variant: 'danger',
        hidden: co.status === 'draft' || co.status === 'void',
      },
      // Delete (draft only)
      {
        label: 'Delete',
        icon: <Trash2 className="h-3.5 w-3.5" />,
        onClick: () => handleDeleteCO(co),
        variant: 'danger',
        disabled: isDeleting,
        hidden: co.status !== 'draft',
      },
    ];

    return (
      <ActionMenu
        id={co._id}
        items={menuItems}
        isOpen={isMenuOpen}
        onOpenChange={open => setActiveMenuId(open ? co._id : null)}
      />
    );
  };

  // Show loading state
  if (isLoading && changeOrders.length === 0) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  // Show project selection required
  if (!projectId) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
        <EmptyState
          icon={Inbox}
          title="No Project Selected"
          description="Please select a project to view change orders."
        />
      </div>
    );
  }

  return (
    <ModuleLayout
      title="Change Orders"
      description="Manage contract scope changes and client approvals"
      icon={<FileText className="h-4 w-4 text-brand-600" />}
      primaryAction={{
        label: 'New CO',
        onClick: handleCreateCO,
        icon: <Plus className="h-3 w-3 mr-1" />,
      }}
      stats={headerStats}
      searchValue={search}
      onSearchChange={value => {
        setSearch(value);
        setPage(1);
      }}
      filters={renderFilters()}
      sortOptions={SORT_OPTIONS}
      sortValue={sortBy}
      onSortChange={setSortBy}
      sortOrder={sortOrder}
      onSortOrderChange={setSortOrder}
      onRefresh={refetch}
      isLoading={isFetching}
      pagination={renderPagination()}
    >
      {changeOrders.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No change orders found"
          description={
            search || status || type || priority
              ? "Try adjusting your filters to find what you're looking for."
              : 'Get started by creating your first change order.'
          }
          action={
            !search && !status && !type && !priority
              ? {
                  label: 'Create Change Order',
                  onClick: handleCreateCO,
                }
              : undefined
          }
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 text-left">
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CO Number
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Request Date
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Schedule Impact
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {changeOrders.map(co => (
                <tr
                  key={co._id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleViewCO(co)}
                >
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-gray-900">{co.coNumber}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-900 truncate max-w-[200px]">
                        {co.title}
                      </span>
                      {co.description && (
                        <span className="text-xs text-gray-500 truncate max-w-[200px]">
                          {co.description}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600">{getTypeLabel(co.type)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-sm font-medium ${
                        co.pricing.isCredit ? 'text-green-600' : 'text-gray-900'
                      }`}
                    >
                      {co.pricing.isCredit ? '-' : ''}
                      {formatCurrency(co.pricing.totalAmount)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge
                      status={getStatusLabel(co.status)}
                      variant={getStatusVariant(co.status)}
                      size="sm"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <PriorityBadge priority={getPriorityVariant(co.priority)} size="sm" />
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600">{formatDate(co.requestDate)}</span>
                  </td>
                  <td className="px-4 py-3">
                    {co.scheduleImpact?.hasImpact ? (
                      <div className="flex items-center space-x-1">
                        <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                        <span className="text-sm text-amber-600">
                          +{co.scheduleImpact.daysAdded} days
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">None</span>
                    )}
                  </td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    {renderRowActions(co)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Change Order Drawer */}
      <ChangeOrderDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        changeOrder={selectedCO}
        mode={drawerMode}
        onSuccess={refetch}
      />

      {/* Send to Client Modal */}
      {sendCO && (
        <SendChangeOrderModal
          isOpen={isSendModalOpen}
          onClose={handleCloseSendModal}
          changeOrder={sendCO}
          projectId={projectId || ''}
          onSuccess={refetch}
        />
      )}
    </ModuleLayout>
  );
};

export default ChangeOrders;
